import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { ManualeButton } from "@/components/ManualeButton";
import { Input } from "@/components/ui/input";
import { PenLine, Search, FilePlus2, Inbox, Clock, CheckCircle2, XCircle, Archive, Users, ChevronRight, FileText, PenSquare, FileStack, FileCheck2 } from "lucide-react";

const SEZIONI = [
  { key: "pratiche-da-firmare", label: "Bozze", icon: Inbox, color: "text-slate-600 bg-slate-50 border-slate-200", statusKey: "BOZZA" },
  { key: "in-attesa", label: "In attesa di firma", icon: Clock, color: "text-amber-600 bg-amber-50 border-amber-200", statusKey: "IN_ATTESA" },
  { key: "firmate", label: "Firmate", icon: CheckCircle2, color: "text-emerald-600 bg-emerald-50 border-emerald-200", statusKey: "FIRMATA" },
  { key: "scadute", label: "Scadute", icon: XCircle, color: "text-orange-600 bg-orange-50 border-orange-200", statusKey: "SCADUTA" },
  { key: "annullate", label: "Annullate", icon: Archive, color: "text-red-600 bg-red-50 border-red-200", statusKey: "ANNULLATA" },
];

export default function SignDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/sign/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const byStatus = (k) => (stats?.transactions_by_status?.[k] ?? 0);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sign-dashboard">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50 to-white p-8 mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-11 w-11 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center">
              <PenLine className="h-6 w-6" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-semibold text-slate-900">Assietica Sign</h1>
              <p className="text-slate-500">Firma e accettazione digitale dei documenti</p>
            </div>
            <div className="ml-auto"><ManualeButton route="/manuale/assietica-sign" /></div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 max-w-3xl mt-6">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input disabled placeholder="Cerca per nominativo, codice fiscale, e-mail, cellulare, ID pratica o ID firma…"
                className="pl-11 h-12 bg-white" data-testid="sign-search-input" />
            </div>
            <Button disabled className="h-12 px-6 bg-sky-600 hover:bg-sky-700 text-white" data-testid="sign-nuova-firma-btn">
              <FilePlus2 className="h-5 w-5 mr-1.5" /> Nuova firma
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <button
            onClick={() => nav("/assietica-sign/clienti")}
            className="w-full flex items-center justify-between rounded-xl border border-slate-200 bg-white p-5 hover:border-sky-300 hover:shadow-sm transition-colors text-left"
            data-testid="sign-clienti-card"
          >
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center">
                <Users className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-slate-800">Clienti / Identity</h3>
                <p className="text-sm text-slate-500">Anagrafiche firmatarie</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-2xl font-bold text-slate-800" data-testid="sign-identities-count">{stats?.identities ?? "—"}</span>
              <ChevronRight className="h-5 w-5 text-slate-400" />
            </div>
          </button>

          <button
            onClick={() => nav("/assietica-sign/documenti")}
            className="w-full flex items-center justify-between rounded-xl border border-slate-200 bg-white p-5 hover:border-sky-300 hover:shadow-sm transition-colors text-left"
            data-testid="sign-documenti-card"
          >
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center">
                <FileText className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-slate-800">Documenti</h3>
                <p className="text-sm text-slate-500">PDF con impronta SHA-256</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-2xl font-bold text-slate-800" data-testid="sign-documents-count">{stats?.documents ?? "—"}</span>
              <ChevronRight className="h-5 w-5 text-slate-400" />
            </div>
          </button>

          <button
            onClick={() => nav("/assietica-sign/firme")}
            className="w-full flex items-center justify-between rounded-xl border border-slate-200 bg-white p-5 hover:border-sky-300 hover:shadow-sm transition-colors text-left"
            data-testid="sign-firme-card"
          >
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center">
                <PenSquare className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-slate-800">Pratiche di firma</h3>
                <p className="text-sm text-slate-500">Crea, invia e monitora</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-2xl font-bold text-slate-800" data-testid="sign-transactions-count">{stats?.transactions ?? "—"}</span>
              <ChevronRight className="h-5 w-5 text-slate-400" />
            </div>
          </button>
        </div>

        <button
          onClick={() => nav("/assietica-sign/questionari")}
          className="w-full flex items-center justify-between rounded-xl border border-sky-200 bg-gradient-to-br from-sky-50 to-white p-5 hover:border-sky-300 hover:shadow-sm transition-colors text-left mb-4"
          data-testid="sign-questionari-card"
        >
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center">
              <FileStack className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-slate-800">Questionari (Web Form)</h3>
              <p className="text-sm text-slate-500">Modelli Compagnia compilabili dal cliente → compilazione, allegati e firma OTP+ID automatica</p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-slate-400" />
        </button>

        <button
          onClick={() => nav("/assietica-sign/consensi")}
          className="w-full flex items-center justify-between rounded-xl border border-sky-200 bg-gradient-to-br from-sky-50 to-white p-5 hover:border-sky-300 hover:shadow-sm transition-colors text-left mb-8"
          data-testid="sign-consensi-card"
        >
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center">
              <FileCheck2 className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-slate-800">Consensi (multicanale)</h3>
              <p className="text-sm text-slate-500">Raccogli i consensi del cliente via SMS/email con conferma tramite codice OTP</p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-slate-400" />
        </button>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SEZIONI.map((s) => (
            <button key={s.key} onClick={() => nav(`/assietica-sign/firme?status=${s.statusKey}`)} className={`rounded-xl border p-5 text-left hover:shadow-sm transition-shadow ${s.color}`} data-testid={`sign-sezione-${s.statusKey}`}>
              <div className="flex items-center gap-2 mb-1">
                <s.icon className="h-5 w-5" />
                <h3 className="font-heading font-semibold text-slate-800 text-sm">{s.label}</h3>
              </div>
              <p className="text-2xl font-bold text-slate-800">{stats ? byStatus(s.statusKey) : "—"}</p>
            </button>
          ))}
        </div>

        <div className="mt-8 rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center" data-testid="sign-wip">
          <p className="text-sm text-slate-500">
            Attive: <strong>Clienti / Identity</strong>, <strong>Documenti</strong> (hash SHA-256),
            <strong> Pratiche di firma</strong> (link cliente, OTP e OTP+documento d'identità) e
            <strong> Questionari Web Form</strong> (compilazione cliente, allegati nel fascicolo e firma automatica).
          </p>
        </div>
      </main>
    </div>
  );
}
